import { useState, useCallback } from 'react';
import { TemplateType, TemplateCategory, TemplateConfig, PlacementSelector, getDefaultConfig } from './types';

export interface UseTemplateStateReturn {
  // State
  selectedTemplate: TemplateType | null;
  config: TemplateConfig;
  templateConfig: TemplateConfig; // Alias for config (for LinkingModule compatibility)
  isModalOpen: boolean;
  previewDevice: 'desktop' | 'mobile';
  currentStep: 1 | 2 | 3;
  activeCategory: TemplateCategory | 'all';
  placementSelector: PlacementSelector | null;
  // Actions
  setSelectedTemplate: (template: TemplateType | null) => void;
  updateConfig: (key: string, value: string | number | boolean) => void;
  setConfig: (config: TemplateConfig) => void;
  resetConfig: () => void;
  openModal: () => void;
  closeModal: () => void;
  saveTemplate: (template: TemplateType, config: TemplateConfig) => void;
  setPreviewDevice: (device: 'desktop' | 'mobile') => void;
  setCurrentStep: (step: 1 | 2 | 3) => void;
  setActiveCategory: (category: TemplateCategory | 'all') => void;
  setPlacementSelector: (placement: PlacementSelector | null) => void;
  savePlacement: (
    selector: string,
    position: 'before' | 'after' | 'inside' | 'inside_start' | 'inside_end',
    description?: string
  ) => void;
}

export function useTemplateState(): UseTemplateStateReturn {
  const [selectedTemplate, setSelectedTemplateState] = useState<TemplateType | null>(null);
  const [config, setConfigState] = useState<TemplateConfig>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewDevice, setPreviewDeviceState] = useState<'desktop' | 'mobile'>('desktop');
  const [currentStep, setCurrentStepState] = useState<1 | 2 | 3>(1);
  const [activeCategory, setActiveCategoryState] = useState<TemplateCategory | 'all'>('all');
  const [placementSelector, setPlacementSelectorState] = useState<PlacementSelector | null>(null);

  const setSelectedTemplate = useCallback((template: TemplateType | null) => {
    setSelectedTemplateState(template);
    if (template) {
      const defaults = getDefaultConfig(template);
      setConfigState(defaults);
    } else {
      setConfigState({});
    }
  }, []);

  const updateConfig = useCallback((key: string, value: string | number | boolean) => {
    setConfigState((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const setConfig = useCallback((newConfig: TemplateConfig) => {
    setConfigState(newConfig);
  }, []);

  const resetConfig = useCallback(() => {
    if (selectedTemplate) {
      const defaults = getDefaultConfig(selectedTemplate);
      setConfigState(defaults);
    } else {
      setConfigState({});
    }
  }, [selectedTemplate]);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
    // Don't reset state when opening - preserve any existing selection
    // Reset only happens via resetConfig or setSelectedTemplate(null)
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Save template and config from modal
  const saveTemplate = useCallback((template: TemplateType, newConfig: TemplateConfig) => {
    setSelectedTemplateState(template);
    setConfigState(newConfig);
    setIsModalOpen(false);
  }, []);

  const setPreviewDevice = useCallback((device: 'desktop' | 'mobile') => {
    setPreviewDeviceState(device);
  }, []);

  const setCurrentStep = useCallback((step: 1 | 2 | 3) => {
    setCurrentStepState(step);
  }, []);

  const setActiveCategory = useCallback((category: TemplateCategory | 'all') => {
    setActiveCategoryState(category);
  }, []);

  const setPlacementSelector = useCallback((placement: PlacementSelector | null) => {
    setPlacementSelectorState(placement);
  }, []);

  const savePlacement = useCallback(
    (
      selector: string,
      position: 'before' | 'after' | 'inside' | 'inside_start' | 'inside_end',
      description?: string
    ) => {
      setPlacementSelectorState({
        selector,
        position,
        description,
        savedAt: Date.now(),
      });
    },
    []
  );

  return {
    // State
    selectedTemplate,
    config,
    templateConfig: config, // Alias for LinkingModule compatibility
    isModalOpen,
    previewDevice,
    currentStep,
    activeCategory,
    placementSelector,
    // Actions
    setSelectedTemplate,
    updateConfig,
    setConfig,
    resetConfig,
    openModal,
    closeModal,
    saveTemplate,
    setPreviewDevice,
    setCurrentStep,
    setActiveCategory,
    setPlacementSelector,
    savePlacement,
  };
}

export default useTemplateState;
